import { makeStyles, Theme } from "@material-ui/core/styles";

type ProfileVariant = "viewer" | "creator";

interface StylesProps {
  variant: ProfileVariant;
}

export const useProfileStyles = makeStyles<Theme, StylesProps>(theme => ({
  indicator: {
    top: 0,
    bottom: "auto",
    height: 6,
    borderRadius: 6,
    backgroundColor: "#F05D94",
  },

  root: {
    color: "#ffffff",
    width: "100%",
    maxWidth: 1031,
    margin: "0 auto",
    padding: "1px 5px 5px 5px",
    [theme.breakpoints.down("xs")]: {
      paddingLeft: "20px",
      paddingRight: "20px",
    },
    [theme.breakpoints.up("sm")]: {
      padding: "1px 5px 5px 5px",
    },
    [theme.breakpoints.up("md")]: {
      paddingTop: "10px",
      paddingRight: "32px",
      paddingLeft: "32px",
      paddingBottom: "32px",
    },
    [theme.breakpoints.up("lg")]: {
      paddingTop: "10px",
      paddingRight: "32px",
      paddingLeft: "32px",
      paddingBottom: "32px",
    },
  },

  breadcrumbs: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    paddingTop: 20,
    marginBottom: theme.spacing(2),
    fontFamily: "'Roboto', sans-serif",
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  breadcrumbHome: {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 300,
    fontSize: "16px",
    color: "#ffffff",
    textDecoration: "none",
    opacity: 0.8,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  breadcrumbSeparator: {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 300,
    fontSize: "16px",
    color: "#ffffff",
    opacity: 0.6,
  },
  breadcrumbActive: {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 500,
    fontSize: "16px",
    color: "#ffffff",
  },

  profileHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(4),
    },
    [theme.breakpoints.up("lg")]: {
      gap: theme.spacing(6),
    },
  },

  firstLastName: {
    fontSize: "20px",
    fontWeight: 600,
    display: "flex",
    gap: "5px",
    alignItems: "center",
    [theme.breakpoints.down(400)]: {
      fontSize: "15px",
    },
  },
  overlayDescription: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: 400,
  },
  overlayHashtags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  overlayHashtag: {
    fontSize: 16,
    fontWeight: 400,
    color: "#FFFFFF",
  },
  headerTop: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      gap: "12px",
    },
    [theme.breakpoints.down(400)]: {
      gap: "12px",
    },
  },

  headerBottom: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      flex: 1,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "1px 10px 5px 10px",
    },
  },

  creatorLinkName: {
    textDecoration: "none",
    color: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
      textDecoration: "none",
      color: "inherit",
      cursor: "pointer",
    },
  },
  /** Хедер профілю: моб (до sm) — 108px; десктоп sm–1439px — 180px; від 1440px — 240px */
  avatar: {
    width: 108,
    height: 108,
    borderRadius: "50%",
    [theme.breakpoints.up("sm")]: {
      width: 180,
      height: 180,
    },
    [theme.breakpoints.up(1440)]: {
      width: 240,
      height: 240,
    },
  },
  avatarWrapper: {
    position: "relative",
    display: "inline-flex",
    borderRadius: "50%",
  },
  avatarEditOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 64,
    height: 64,
    borderRadius: "50%",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    cursor: "pointer",
    padding: 0,
    zIndex: 3,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.36)",
    },
    [theme.breakpoints.down("xs")]: {
      width: 32,
      height: 32,
    },
  },
  avatarEditIcon: {
    width: 40,
    height: 40,
    objectFit: "contain",
    [theme.breakpoints.down("xs")]: {
      width: 20,
      height: 20,
    },
  },

  userInfo: {
    flex: 1,
    width: "100%",
    textAlign: "center",
    marginTop: 0,
    [theme.breakpoints.up("sm")]: {
      textAlign: "left",
    },
  },

  /** Чужий профіль: зарезервовано під модифікатори; верхній відступ 0 */
  userInfoWhenNotOwner: {
    marginTop: 0,
  },

  userName: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "30px",
    flex: 1,
    marginTop: 0,
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "35px",
      flexWrap: "wrap",
      width: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      gap: "12px",
    },
  },
  bannerEditButton: {
    height: "60px",
    borderRadius: "6px",
    border: "1px solid #FE5A97",
    backgroundColor: "transparent",
    color: "#FFFFFF",
    textTransform: "none",
    fontSize: "20px",
    fontWeight: 600,
    padding: "0 20px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "transparent",
      border: "1px solid #FE5A97",
    },
    "& .MuiButton-startIcon": {
      marginRight: 0,
      "& svg": {
        color: "#FFFFFF",
        fontSize: "24px",
      },
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  profileActionButton: {
    height: "48px",
    borderRadius: "6px",
    border: "1px solid #FE5A97",
    backgroundColor: "transparent",
    color: "#FFFFFF",
    textTransform: "none",
    fontSize: "18px",
    fontWeight: 600,
    padding: "0 18px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "transparent",
      border: "1px solid #FE5A97",
    },
    [theme.breakpoints.down("sm")]: {
      height: "36px",
      fontSize: "14px",
      padding: "0 12px",
    },
  },
  profileEditActions: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-start",
    },
  },

  buttonActionIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 52,
    height: 52,
    cursor: "pointer",
    borderRadius: "50%",
    border: "1px solid #FE5A97",
    backgroundColor: "transparent",
    transition: "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "#FE5A97",
    },
  },

  nameContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    // width: "220px",
    columnGap: "20px",
  },

  verifiedIcon: {
    color: "#FE5A97",
  },
  nameContainerAge: {
    marginTop: "80px",
    [theme.breakpoints.down("xs")]: {
      marginTop: "40px",
    },
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    marginTop: 0,
    [theme.breakpoints.up("sm")]: {
      gap: "12px",
    },
    [theme.breakpoints.down("sm")]: {
      flexWrap: "nowrap",
    },
  },

  shareButtonHiddenOnOwnProfile: {
    display: "none",
  },

  shareButton: {
    minWidth: 56,
    width: 56,
    height: 48,
    borderRadius: 6,
    border: "none",
    backgroundColor: "#131313",
    color: "#FFFFFF",
    padding: 0,
    "&:hover": {
      backgroundColor: "#131313",
    },
    "& img": {
      width: 24,
      height: 24,
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: 32,
      width: 32,
      height: 28,
      backgroundColor: "#131313",
      "&:hover": {
        backgroundColor: "#131313",
      },
      "& img": {
        width: 18,
        height: 18,
      },
    },
  },

  followButton: {
    backgroundColor: "#FE5A97",
    color: "#FFFFFF",
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "14px",
    fontWeight: 700,
    height: "48px",
    width: "243px",
    border: "1px solid #FE5A97",
    [theme.breakpoints.down("sm")]: {
      width: "108px",
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.25, 0.75),
      fontSize: "0.7rem",
    },
  },

  subscribeButton: {
    backgroundColor: "#FE5A97",
    color: "#FFFFFF",
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "14px",
    fontWeight: 700,
    height: "48px",
    width: "243px",
    border: "1px solid #FE5A97",
    [theme.breakpoints.down("sm")]: {
      width: 100,
      height: 28,
      minHeight: 28,
      fontSize: "12px",
      padding: theme.spacing(0.25, 0.5),
      lineHeight: 1,
    },
    "&:hover": {
      backgroundColor: "rgba(254, 90, 151, 0.9)",
    },
    "& .MuiButton-label": {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.5),
      "& svg": {
        width: 16,
        height: 16,
      },
    },
  },
  subscribeButtonSingleFree: {
    backgroundColor: "#FFFFFF",
    color: "#BE3265",
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "14px",
    fontWeight: 700,
    height: "48px",
    width: "243px",
    border: "1px solid #FFFFFF",
    [theme.breakpoints.down("sm")]: {
      width: 100,
      height: 28,
      minHeight: 28,
      fontSize: "12px",
      padding: theme.spacing(0.25, 0.5),
      lineHeight: 1,
    },
    "&:hover": {
      backgroundColor: "#FFFFFF",
    },
    "& .MuiButton-label": {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  subscribeButtonSubscribed: {
    backgroundColor: "#131313",
    color: "#FFFFFF",
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "14px",
    fontWeight: 700,
    height: "48px",
    width: "243px",
    border: "1px solid #363636",
    [theme.breakpoints.down("sm")]: {
      width: 100,
      height: 28,
      minHeight: 28,
      fontSize: "12px",
      padding: theme.spacing(0.25, 0.5),
      lineHeight: 1,
    },
    "&:hover": {
      backgroundColor: "#1E1E1E",
    },
    "& .MuiButton-label": {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  subscribeDropdownMenu: {
    "& .MuiPaper-root": {
      backgroundColor: "#131313",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "12px",
      minWidth: 400,
      marginTop: 65,
    },
  },

  buttonAction: {
    backgroundColor: "#FFFFFF08",
    color: "#fff",
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "14px",
    width: "243px",
    height: "48px",
    border: "1px solid #FE5A97",
    "&:hover": {
      backgroundColor: "#FE5A97",
    },
    "&:disabled": {
      backgroundColor: "#EFEFEF",
    },
    "&.Mui-disabled": {
      color: "#F05D94",
    },
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.5),
      "& svg": {
        width: 16,
        height: 16,
      },
    },
    [theme.breakpoints.down("sm")]: {
      width: "108px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.25, 0.75),
      fontSize: "0.7rem",
      "& .MuiButton-startIcon": {
        marginRight: theme.spacing(0.25),
        "& svg": {
          width: 14,
          height: 14,
        },
      },
    },
  },
  buttonActionModal: {
    backgroundColor: "#EFEFEF",
    color: "#F05D94",
    textTransform: "none",
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "14px",
    width: "105px",
    height: "28px",
    "&:hover": {
      backgroundColor: "rgba(240,110,196,0.1)",
    },
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.5),
      "& svg": {
        width: 16,
        height: 16,
      },
    },
    [theme.breakpoints.down("sm")]: {
      MaxWidth: "105px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.25, 0.75),
      fontSize: "0.7rem",
      "& .MuiButton-startIcon": {
        marginRight: theme.spacing(0.25),
        "& svg": {
          width: 14,
          height: 14,
        },
      },
    },
  },
  followButtonModal: {
    backgroundColor: "#F05D94",
    color: "#FFFFFF",
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    borderRadius: "6px",
    boxShadow: "none",
    fontSize: "0.85rem",
    width: "80px",
    height: "28px",
    [theme.breakpoints.down("sm")]: {
      width: "60px",
    },
    "&:hover": {
      backgroundColor: "#d155a9",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.25, 0.75),
      fontSize: "0.7rem",
    },
  },
  nameButSup: {
    fontSize: "14px",
    fontWeight: 500,
    [theme.breakpoints.down(400)]: {
      fontSize: "12px",
    },
  },

  subscribeButtonText: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#FFFFFF",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    lineHeight: 1.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      fontWeight: 600,
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "16px",
      fontWeight: 600,
    },
  },
  subscribeButtonTextInner: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    "& svg": {
      display: "block",
    },
    [theme.breakpoints.down("sm")]: {
      gap: 4,
      "& svg": {
        width: 10,
        height: 10,
        minWidth: 10,
        minHeight: 10,
      },
    },
  },

  creatorMeta: {
    width: "100%",
    marginTop: 4,
    textAlign: "left",
    [theme.breakpoints.up("sm")]: {
      textAlign: "left",
    },
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },

  stats: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: "20px",
    "& > *": {
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.875rem",
      },
    },
  },

  copyButton: {
    display: "flex",
    rowGap: "5px",
    borderColor: "#FE5A97",
    color: "#FE5A97",
    textTransform: "none",
    padding: theme.spacing(0.5, 2),
    width: "100%",
    boxSizing: "border-box",
    marginTop: "5px",
    "&:hover": {
      backgroundColor: "rgba(240,110,196,0.08)",
    },
    [theme.breakpoints.up("sm")]: {
      width: "auto",
      // marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
      border: "none",
      padding: "0",
    },
  },

  description: {
    // marginTop: theme.spacing(1),
    fontSize: "14px",
    fontWeight: 400,
    color: "#FFFFFF",
    [theme.breakpoints.up("md")]: {
      fontSize: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },
  website: {
    marginTop: theme.spacing(1),
    fontSize: "14px",
    fontWeight: 500,
    color: "#FE5A97",
    [theme.breakpoints.up("md")]: {
      fontSize: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },

  link: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: "#f06ec4",
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "14px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },

  tabs: {
    borderTop: "2px solid #FFFFFF33",
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      borderTop: "1px solid #FFFFFF33",
      overflowX: "auto",
      "& .MuiTabs-flexContainer": {
        flexWrap: "nowrap",
      },
      "& .MuiTab-root": {
        minWidth: "83px",
        padding: theme.spacing(0, 1),
      },
    },
    [theme.breakpoints.up("md")]: {
      marginTop: theme.spacing(4),
    },
    [theme.breakpoints.up("lg")]: {
      marginTop: theme.spacing(6),
    },
    "& .MuiTab-root": {
      minHeight: 48,
    },
    "& .MuiTab-wrapper": {
      marginBottom: 0,
      "& > *:first-child": {
        marginBottom: 0,
      },
    },
    "& .MuiTabs-indicator": {
      top: "auto",
      bottom: 0,
      height: 2,
      borderRadius: 2,
      backgroundColor: "#FFFFFF33",
      [theme.breakpoints.down("xs")]: {
        height: 1,
        borderRadius: 1,
      },
    },
  },

  /** Маска під SVG-іконки вкладок; колір змінюється для active / inactive */
  tabBarIcon: {
    width: 15,
    height: 15,
    display: "inline-block",
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
    maskSize: "contain",
    maskRepeat: "no-repeat",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    transition: "background-color 0.2s ease",
  },

  tab: {
    textTransform: "none",
    color: "#ccc",
    fontFamily: "'TT Norms', sans-serif",
    fontWeight: 700,
    fontSize: "14px",
    minHeight: 48,
    padding: theme.spacing(1.5, 2),
    "& .MuiTab-wrapper": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "row",
      gap: "10.5px",
      fontFamily: "'TT Norms', sans-serif",
      fontWeight: 700,
      fontSize: "14px",
      marginBottom: 0,
    },
    "& svg": {
      width: 15,
      height: 15,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
      padding: theme.spacing(1, 1.2),
      "& .MuiTab-wrapper": {
        fontSize: "12px",
      },
    },
    "&.Mui-selected": {
      color: "#F05D94",
    },
    "&.Mui-selected $tabBarIcon": {
      backgroundColor: "#FE5A97",
    },
  },

  postsGrid: {
    marginTop: "40px",
    display: "flex",
    flexWrap: "wrap",
    // gap: 20,
    [theme.breakpoints.down("xs")]: {
      marginTop: "15px",
    },
  },

  postItem: {
    paddingRight: 10,
    paddingBottom: 10,
    [theme.breakpoints.down("xs")]: {
      paddingRight: 6,
      paddingBottom: 6,
    },
  },

  postImg: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    objectPosition: "top",
    display: "block",
    [theme.breakpoints.up("sm")]: {
      transition: "opacity 0.3s",
      "&:hover": {
        opacity: 0.9,
      },
    },
    [theme.breakpoints.down("xs")]: {
      maxHeight: "350px",
      height: "190px",
    },
  },
  postVideo: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "contain",
    // objectPosition: "top",
    display: "block",
    [theme.breakpoints.up("sm")]: {
      transition: "opacity 0.3s",
      "&:hover": {
        opacity: 0.9,
      },
    },
    [theme.breakpoints.down("xs")]: {
      maxHeight: "350px",
      height: "190px",
    },
  },

  postPlaceholder: {
    width: "100%",
    paddingTop: "100%",
    backgroundColor: "#ccc",
    [theme.breakpoints.up("sm")]: {
      width: 290,
      height: 290,
      paddingTop: 0,
    },
  },

  requestCustomVideoBtn: {
    color: "#FE5A97",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "18px",
    marginTop: "16px",
    marginLeft: "16px",
    cursor: "pointer",
    border: "1px solid #FE5A97",
  },

  dialogPaper: {
    backgroundColor: "#000",
    color: "#fff",
    borderRadius: 8,
    width: "90vw",
    maxWidth: 1000,
    maxHeight: "90vh",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      height: "100%",
      maxHeight: "100vh",
      margin: 0,
      borderRadius: 0,
      padding: 0,
    },
  },
  dialogContent: {
    padding: 0,
    [theme.breakpoints.down("xs")]: {
      height: "100vh",
      padding: 0,
      margin: 0,
    },
  },
  mediaSection: {
    flex: "0 0 60%",
    position: "relative",
    backgroundColor: "#000",
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 70%",
    },
  },
  detailsSection: {
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      backgroundColor: "transparent",
    },
    [theme.breakpoints.down("xs")]: {
      overflowY: "auto",
    },
  },

  creatorHeader: {
    height: "55px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginTop: "16px",
    marginRight: 0,
    borderBottom: "1px solid white",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      justifyContent: "space-between",
      borderBottom: "none",
    },

    [theme.breakpoints.down("xs")]: {
      margin: "4px",
      padding: "4px",
      gap: "4px",
      flexWrap: "wrap",
    },
  },

  buttonTip: {
    backgroundColor: "transparent",
    border: "1px solid #f06ec4",
    color: "#f06ec4",
    display: "flex",
    maxHeight: "28px",
    minWidth: "126px",
  },

  followBtn: {
    color: "#0095F6",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "18px",
    maxWidth: "80px",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "60px",
    },
  },
  commentFooter: {
    lineHeight: "100%",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  commentFooterText: {
    color: "#737373",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "18px",
  },

  commentReplyButton: {
    color: "#737373",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "18px",
    paddingRight: "16px",
    "&:hover": {
      color: "#0095F6",
    },
    cursor: "pointer",
  },

  creatorName: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    [theme.breakpoints.down("sm")]: {
      gap: "3px",
    },
  },
  nameLink: {
    textDecoration: "none",
    color: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
      textDecoration: "none",
      color: "inherit",
      cursor: "pointer",
    },
  },
  creatorNameFirdtLast: {
    display: "flex",
    alignItems: "center",
    columnGap: "4px",
    marginLeft: "4px",
  },
  creatorButtonHelpHelp: {
    display: "flex",
    columnGap: "4px",
  },

  slide: {
    outline: "none",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  paginationDots: {
    position: "absolute",
    bottom: theme.spacing(1),
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: 4,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.4)",
  },
  dotActive: {
    backgroundColor: "#fff",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderBottom: "1px solid #333",
  },
  commentsListWrapper: {
    display: "flex",
    position: "relative",
    height: "auto",
    [theme.breakpoints.down("md")]: {
      // maxHeight: "100%",
    },
  },
  commentsList: {
    position: "relative",
    overflow: "auto",
    maxHeight: "calc(50vh - 84px)",
    padding: "12px 20px 0 20px",
    [theme.breakpoints.down("md")]: {
      // maxHeight: "calc(90vh - 180px)",
    },
    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(90vh - 160px)",
    },
    "&::-webkit-scrollbar": {
      width: "4px",
    },
  },
  commentItem: {
    display: "flex",
    alignItems: "flex-start",
    width: "100hv",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(0.5),
    },
  },
  commentBody: {
    flex: 1,
    [theme.breakpoints.down("xs")]: {
      fontSize: "13px",
    },
  },

  commentAuthor: {
    fontWeight: "bold",
    fontSize: "18px",
    lineHeight: "100%",
    [theme.breakpoints.down("xs")]: {
      fontSize: "13px",
    },
  },

  commentText: {
    fontSize: "16px",
    lineHeight: "110%",
    padding: "8px 0",
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },

  likeBtn: { padding: 2 },

  showAllText: {
    cursor: "pointer",
    color: "#0095F6",
    //  marginTop: theme.spacing(1),
    textAlign: "left",
    //  padding: theme.spacing(1),
    fontSize: "14px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
      padding: theme.spacing(0.5),
    },
  },

  replyContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },

  commentMeta: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  postStats: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: "1px solid white",
    borderBottom: "1px solid white",
    "& svg": {
      marginRight: 4,
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.5, 0),
    },
  },

  postsStatsDiv: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: "8px",
    [theme.breakpoints.down("xs")]: {
      paddingLeft: "4px",
    },
  },

  postStatsFirst: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  actionIcons: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },

  commentInput: {
    flex: 1,
    backgroundColor: "transparent",
    color: "white",
    border: "none",
    outline: "none",
    fontSize: "14px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
    "&::placeholder": {
      color: "#737373",
    },
  },

  commentInputContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: 20,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.25),
    },
  },

  postButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "12px",
    padding: "4px 8px",
    minWidth: "auto",
    backgroundColor: "#FE5A97",
    "&.Mui-disabled": {
      backgroundColor: "#747474",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "10px",
      padding: "2px 4px",
    },
  },

  inputField: {
    color: "white",
    border: "1px solid transparent",
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "& .MuiOutlinedInput-input": {
      color: "#fff",
      fontSize: "16px",
      fontWeight: "400",
      padding: "10px",
    },
    "& .MuiOutlinedInput-input::placeholder": {
      color: "#737373",
      opacity: 1,
    },
  },

  inputIcon: {
    color: "white",
  },

  loadingContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  postMeta: {
    display: "flex",
    gap: "10px",
    marginTop: "16px",
    marginBottom: "16px",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      marginLeft: "4px",
      marginBottom: "4px",
      gap: "4px",
      padding: "4px",
    },
  },

  postMetaText: {
    flex: 1,
    [theme.breakpoints.down("xs")]: {
      fontSize: "14px",
    },
  },

  postBtn: {
    color: "#FE5A97",
  },

  smallAvatar: {
    width: 32,
    height: 32,
    [theme.breakpoints.down("xs")]: {
      width: 24,
      height: 24,
      marginRight: theme.spacing(0.5),
    },
  },

  statsText: {
    fontSize: "14px",
    color: "#FFFFFF",
    marginLeft: theme.spacing(0.5),
    [theme.breakpoints.down("xs")]: {
      fontSize: "12px",
    },
  },

  commentsPreview: {
    marginTop: theme.spacing(1),
    color: "#737373",
    cursor: "pointer",
    fontSize: "14px",
    "&:hover": {
      color: "#fff",
    },
  },
  purchaseButton: {
    position: "absolute",
    bottom: "50%",
    left: "50%",
    transform: "translate(-50%, 50%)",
    zIndex: 4,
    padding: "10px 20px",
    color: "#FE5A97",
    border: "1px solid #FE5A97",
    borderRadius: "5px",
    fontStyle: "normal",
    fontWeight: 800,
    fontSize: "12px",
    textTransform: "none",
    width: "90%",
    [theme.breakpoints.down("sm")]: {
      height: "30px",
      width: "110px",
      fontSize: "9px",
      fontWeight: 400,
    },
  },
  priceBlockIcon: {
    pointerEvents: "none",
    padding: 0,
    borderRadius: "50%",
    width: "51px",
    height: "51px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF1A",
    color: "#FFFFFF",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1,
    [theme.breakpoints.down("sm")]: {
      width: "20px",
      height: "20px",
      fontSize: "12px",
    },
  },
  priceBlock: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 4,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    textAlign: "center",
    color: "#fff",
    fontStyle: "normal",
    "& span": {
      fontSize: "20px",
      fontWeight: 400,
      lineHeight: "1.2",
      "& .price-text": {
        fontWeight: 700,
      },
      "& .price-amount": {
        fontWeight: 700,
      },
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& span": {
        fontSize: "10px",
      },
    },
  },
  unlockButton: {
    position: "absolute",
    bottom: "10%",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 4,
    padding: "10px 20px",
    backgroundColor: "#FE5A97",
    color: "white",
    borderRadius: "5px",
    fontStyle: "normal",
    fontWeight: 800,
    fontSize: "12px",
    textTransform: "none",
    width: "90%",
    [theme.breakpoints.down("sm")]: {
      height: "30px",
      width: "110px",
      fontSize: "9px",
      fontWeight: 400,
    },
  },
  modalSubscriptionWrapper: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      zIndex: 1400,
    },
  },

  modalSubscription: {
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      zIndex: 1400,
    },
  },
  modelSubscriptionContent: {
    height: "300px",
    width: "100%",
    backgroundColor: "#131313",
    border: "1px solid #363636",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    [theme.breakpoints.down("sm")]: {
      padding: "8px",
      height: "auto",
      minHeight: "300px",
    },
  },
  modelSubscriptionHead: {
    //  border: "1px solid #363636",
    backgroundColor: "#131313",
    display: "flex",
    justifyContent: "center",
    color: "#FE5A97",
  },
  modelSubscriptionFooter: {
    //  border: "1px solid #363636",
    backgroundColor: "#131313",
    display: "flex",
    justifyContent: "space-between",
  },
  subscribeRadio: {
    display: "flex",
    border: "1px solid #EDECED33",
    color: "#FFFFFF",
    height: "80px",
    width: "450px",
    margin: "8px 0",
    [theme.breakpoints.down("sm")]: {
      width: "calc(100% - 32px)",
      marginLeft: "16px",
      marginRight: "16px",
    },
    // Видаляємо margin з FormControlLabel з максимальною специфічністю
    "& .MuiFormControlLabel-root.MuiFormControlLabel-root": {
      marginLeft: "0px !important",
      marginRight: "0px !important",
    },

    // Стилізація радіо-кнопок з рожевим кольором тільки для цього модального вікна
    "& .MuiRadio-root": {
      color: "#FE5A97",
      "&.Mui-checked": {
        color: "#FE5A97",
      },
    },
    "& .MuiRadio-colorSecondary.Mui-checked": {
      color: "#FE5A97",
    },
  },
  modalTitle: {
    backgroundColor: "#131313",
    color: "#FE5A97",
    textAlign: "center",
    fontSize: "24px",
    padding: "20px 0",
    borderBottom: "none",
  },

  modalContent: {
    backgroundColor: "#131313",
    color: "#fff",
    padding: "0 24px 24px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
    [theme.breakpoints.down("sm")]: {
      flex: 1,
      minHeight: 0,
      width: "100%",
      alignItems: "stretch",
      justifyContent: "flex-start",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
    },
  },

  /** Список тарифів у модалці підписки — обмеження по висоті екрана + внутрішній скрол */
  subscribePlansList: {
    display: "flex",
    gap: 16,
    marginTop: 24,
    width: "100%",
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    maxHeight: "calc(100dvh - 150px)",
  },

  modalSubtitle: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#FFFFFF",
    marginBottom: "16px",
    lineHeight: "1.3",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
    },
  },

  tipInputContainer: {
    marginTop: "8px",
  },

  tipLabel: {
    marginBottom: "8px",
    fontSize: "14px",
  },

  tipInput: {
    "& input": {
      color: "#fff",
      backgroundColor: "#1e1e1e",
      MozAppearance: "textfield", // Firefox
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
    // Вимикає стрілки в Chrome/Safari
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  },
  modelSubscriptionWrapper: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#131313",
    padding: "0px",
    width: "550px",
    borderRadius: "12px",
    zIndex: 1401, // має бути вищий за overlay
    boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
    border: "none",
    [theme.breakpoints.down("sm")]: {
      width: "90%",
      margin: "0 auto",
      maxHeight: "90vh",
      overflow: "auto",
    },
  },
  modalTipWrapper: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#131313",
    padding: "0px",
    width: "100%",
    maxWidth: "838px",
    borderRadius: "12px",
    zIndex: 1401, // має бути вищий за overlay
    boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
    border: "none",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      left: 0,
      top: 0,
      transform: "none",
      bottom: 0,
      right: 0,
      maxWidth: "100%",
      borderRadius: 0,
      height: "100%",
      maxHeight: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      "& > .MuiBox-root:first-child": {
        flexShrink: 0,
      },
    },
  },
  modalActions: {
    backgroundColor: "#131313",
    padding: "16px",
    justifyContent: "space-between",
    margin: "0 auto",
    maxWidth: 755,
  },

  cancelButton: {
    color: "#FFFFFF",
    backgroundColor: "#FE5A97",
    border: "1px solid #FE5A97",
    borderRadius: 6,
    fontSize: "20px",
    fontWeight: 600,
    padding: "10px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#E5497F",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "16px",
    },
  },

  sendButton: {
    color: "#FFFFFF",
    backgroundColor: "transparent",
    border: "1px solid #FE5A97",
    borderRadius: 6,
    textTransform: "none",
    fontSize: "20px",
    fontWeight: 600,
    padding: "10px 24px",
    "&:hover": {
      backgroundColor: "rgba(254, 90, 151, 0.08)",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "16px",
    },
  },

  sendButtonPrimary: {
    color: "#FFFFFF",
    backgroundColor: "#FE5A97",
    borderRadius: 6,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#E5497F",
    },
  },

  unlockedLabel: {
    position: "absolute",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 4,
    padding: "0px",
    color: "#FE5A97",
    border: "1px solid #FE5A97",
    borderRadius: "5px",
    fontStyle: "normal",
    fontWeight: 900,
    fontSize: "15px",
    textTransform: "none",
    height: "25px",
    width: "65px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "30px",
      fontSize: "8px",
      fontWeight: 600,
    },
  },
  creatorNameHelpHelp: {
    display: "flex",
  },
  editPostTextField: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      borderRadius: 8,
      "& fieldset": {
        borderColor: "#1F1F1F",
      },
      "&:hover fieldset": {
        borderColor: "#1F1F1F",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1F1F1F",
      },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#fff",
      opacity: 0.7,
    },
    "& .MuiInputLabel-root": {
      color: "#fff",
    },
    "& .MuiFormHelperText-root": {
      color: "#747474",
      marginBottom: 0,
    },
    "& .MuiSelect-icon": {
      color: "#FFFFFF",
      right: 10,
    },
  },
  editPostModalTitle: {
    backgroundColor: "#131313",
    color: "#FFFFFF",
    fontSize: "32px",
    fontWeight: 700,
    padding: "20px 24px 12px 24px",
    borderBottom: "1px solid #FFFFFF14",
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
      padding: "16px 16px 10px 16px",
    },
  },
  editPostModalContent: {
    backgroundColor: "#131313",
    color: "#fff",
    padding: "24px",
    borderRadius: 12,
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      padding: "12px",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
    },
  },
  editPostModalWrapper: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#131313",
    padding: 0,
    width: "100%",
    maxWidth: "1182px",
    maxHeight: "calc(100dvh - 48px)",
    borderRadius: "12px",
    zIndex: 1401,
    boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
    border: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      transform: "none",
      borderRadius: 0,
      height: "100%",
      maxHeight: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
  },
  editPostLayout: {
    display: "grid",
    gridTemplateColumns: "1.15fr 0.95fr",
    gap: 24,
    alignItems: "stretch",
    minHeight: 620,
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: 14,
      minHeight: "auto",
      height: "auto",
    },
  },
  editPostMediaPane: {
    width: "100%",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #FFFFFF14",
    backgroundColor: "#0D0D0D",
    minHeight: 0,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 220,
      height: 220,
    },
  },
  editPostMediaImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  editPostMediaVideo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    backgroundColor: "#000",
  },
  editPostFormPane: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "100%",
    "& > *": {
      marginTop: 0,
      marginBottom: 0,
    },
    "& > * + *": {
      marginTop: 24,
    },
  },
  editPostCreatorHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: "1px solid #FFFFFF14",
  },
  editPostCreatorAvatar: {
    width: 48,
    height: 48,
  },
  editPostCreatorName: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.1,
    color: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
    },
  },
  editPostCreatorUsername: {
    fontSize: 14,
    fontWeight: 500,
    color: "#FE5A97",
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
    },
  },
  editPostDeleteText: {
    marginTop: 8,
    color: "#FE5A97",
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.1,
    textDecoration: "underline",
    cursor: "pointer",
    alignSelf: "flex-start",
    background: "none",
    border: "none",
    padding: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  editPostActions: {
    marginTop: "auto",
    paddingTop: "16px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "center",
    gap: 12,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      paddingTop: "12px",
    },
  },
  editPostCancelButton: {
    color: "#FFFFFF",
    backgroundColor: "transparent",
    border: "1px solid #FE5A97",
    borderRadius: 8,
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 600,
    minHeight: 52,
    "&:hover": {
      backgroundColor: "rgba(254, 90, 151, 0.08)",
    },
    [theme.breakpoints.down(500)]: {
      minHeight: 48,
      fontSize: "14px",
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: 38,
    },
  },
  editPostSaveButton: {
    color: "#FFFFFF",
    backgroundColor: "#FE5A97",
    border: "1px solid #FE5A97",
    borderRadius: 8,
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 700,
    minHeight: 52,
    "&:hover": {
      backgroundColor: "#E5497F",
    },
    [theme.breakpoints.down(500)]: {
      minHeight: 48,
      fontSize: "14px",
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: 38,
    },
  },
  categoryRemoveLabel: {
    color: "#FFD0E1",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  editPostCategorySelect: {
    "& .MuiOutlinedInput-root": {
      minHeight: 40,
      backgroundColor: "#121212",
      borderRadius: 8,
      "& fieldset": {
        borderColor: "#2E2E2E",
      },
      "&:hover fieldset": {
        borderColor: "#3A3A3A",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FE5A97",
      },
    },
    "& .MuiSelect-root": {
      fontSize: 14,
      color: "#FFFFFF",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiSelect-select": {
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 12,
      paddingRight: 28,
    },
    [theme.breakpoints.down("xs")]: {
      "& .MuiOutlinedInput-root": {
        minHeight: 36,
      },
      "& .MuiSelect-root": {
        fontSize: 14,
      },
      "& .MuiSelect-select": {
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 10,
      },
    },
  },
  deletePostModalWrapper: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: 560,
    backgroundColor: "#131313",
    borderRadius: 12,
    boxShadow: "0px 4px 20px rgba(0,0,0,0.5)",
    border: "1px solid #FFFFFF14",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: "calc(100% - 24px)",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
  },
  deletePostHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px",
  },
  deletePostTitle: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: 700,
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
    },
  },
  deletePostClose: {
    color: "#FFFFFF",
    padding: 6,
  },
  deletePostBody: {
    padding: "14px 20px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [theme.breakpoints.down("sm")]: {
      paddingTop: 2,
    },
  },
  deletePostPreview: {
    position: "relative",
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #FFFFFF14",
    backgroundColor: "#0D0D0D",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  deletePostPreviewImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  deletePostPreviewMediaPaid: {
    filter: "brightness(0.55)",
  },
  deletePostPaidOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    pointerEvents: "none",
  },
  deletePostPaidLabel: {
    color: "#FFFFFF",
    fontSize: 25,
    fontWeight: 400,
    lineHeight: 1.15,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
    },
  },
  deletePostPaidAmount: {
    color: "#FFFFFF",
    fontSize: 25,
    fontWeight: 700,
    lineHeight: 1.1,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
    },
  },
  deletePostQuestion: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
      fontWeight: 700,
    },
  },
  deletePostHint: {
    color: "#FFFFFFCC",
    fontSize: 20,
    fontWeight: 400,
    lineHeight: 1.35,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
    },
  },
  deletePostActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "0 20px 20px 20px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "flex-start",
      width: "100%",
    },
  },
  deletePostCancelBtn: {
    border: "1px solid #FE5A97",
    borderRadius: 8,
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 20,
    height: 48,
    width: "100%",
    maxWidth: 200,
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      maxWidth: "none",
      flex: 1,
    },
  },
  deletePostDeleteBtn: {
    border: "1px solid #FE5A97",
    borderRadius: 8,
    backgroundColor: "#FE5A97",
    color: "#FFFFFF",
    textTransform: "none",
    fontWeight: 600,
    fontSize: 20,
    height: 48,
    width: "100%",
    maxWidth: 200,
    "&:hover": {
      backgroundColor: "#E5497F",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      maxWidth: "none",
      flex: 1,
    },
  },
}));
